import type { TVirtualizedTableOptions } from './components/VirtualizedTable';

/**
 * File type enumeration for supported file formats
 */
export enum EFileType {
  IMG = 'IMG',
  GIF = 'GIF',
  PDF = 'PDF',
  CSV = 'CSV',
  TXT = 'TXT',
  JSON = 'JSON',
  YML = 'YML',
  PYTHON = 'PYTHON',
  EXCEL = 'EXCEL',
  FEATHER = 'FEATHER',
  DOCX = 'DOCX',
  HTML = 'HTML',
  MARKDOWN = 'MARKDOWN',
  EML = 'EML',
  AUDIO = 'AUDIO',
  VIDEO = 'VIDEO',
  UNSUPPORTED = 'UNSUPPORTED',
  UNKNOWN = 'UNKNOWN',
}

/**
 * File metadata interface
 */
export interface IFileMetadata {
  contentType?: string;
  fileSize?: number;
  [key: string]: unknown;
}

/**
 * File renderer configuration
 */
export interface IFileRendererConfig {
  expandImage?: boolean;
  pdfZoom?: number;
  showLineNumbers?: boolean;
  maxHeight?: string;
  /** Borderless, syntax-colored treatment used inside the output modal. */
  outputPresentation?: boolean;
  /** Scale applied to document-like previews embedded in constrained cards. */
  previewScale?: number;
  /**
   * The host is a fixed-size tile: fill it exactly instead of keeping the
   * usual minimum height. An HTML page laid out in a taller frame than the
   * tile shows has its lower part (or anything it centres) cut off.
   */
  fillContainer?: boolean;
  /** Table treatment for spreadsheet files (CSV, Excel), e.g. the output modal's. */
  tableProps?: TVirtualizedTableOptions;
  /** Show only the first sheet of a workbook, with no tab strip, e.g. in a small preview card. */
  firstSheetOnly?: boolean;
  /** Draw nothing while loading; the host shows its own loading state (a skeleton). */
  hideLoadingIndicator?: boolean;
}

/**
 * Main FileRenderer component props
 */
export interface IFileRendererProps {
  blob: Blob;
  fileName: string;
  metadata?: IFileMetadata;
  config?: IFileRendererConfig;
  /** When set, PDF virtualization scrolls within this element instead of an inner scroller. */
  scrollParent?: HTMLElement | null;
  /** Whether this renders inside the output preview modal rather than an inline card. */
  isExpanded?: boolean;
}

/**
 * Individual renderer props - shared by all file type renderers
 */
export interface IBaseRendererProps {
  blob: Blob;
  fileName: string;
  config?: IFileRendererConfig;
  /** When set, PDF virtualization scrolls within this element instead of an inner scroller. */
  scrollParent?: HTMLElement | null;
  /** Whether this renders inside the output preview modal rather than an inline card. */
  isExpanded?: boolean;
}
